import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Col, Row } from 'reactstrap';

interface BreadcrumbItem {
  label: string;
  path?: string;
  isActive?: boolean;
}

interface BreadCrumbProps {
  title: string;
  pageTitle?: string;
  parentRoute?: string;
  breadcrumbItems?: BreadcrumbItem[];
}

const normalizeLabel = (label?: string) => {
  const trimmedLabel = label?.trim();

  if (
    !trimmedLabel ||
    trimmedLabel === '-' ||
    trimmedLabel.toLowerCase() === 'null' ||
    trimmedLabel.toLowerCase() === 'undefined'
  ) {
    return '';
  }

  return trimmedLabel;
};

const titleCaseEntity = (label: string) =>
  label === 'Lands' ? 'Land' : label === 'Properties' ? 'Property' : label;

const getBrowserTitleParts = (
  items: BreadcrumbItem[],
  title: string,
  hasCustomBreadcrumbItems: boolean,
) => {
  const validItems = items.filter(item => normalizeLabel(item.label));
  const hierarchy = validItems
    .map(item => normalizeLabel(item.label))
    .filter(label => label && label !== 'Dashboard');
  const pageTitle = normalizeLabel(title);
  const mazarName = hierarchy[0];
  const lastItem = validItems[validItems.length - 1];
  const activeItem = [...validItems].reverse().find(item => item.isActive);
  const activeLabel = normalizeLabel(activeItem?.label);
  const lastLabel = normalizeLabel(lastItem?.label);
  const activePath = activeItem?.path || lastItem?.path || '';
  const isLandDetailPath = /\/lands\/[^/?]+/.test(activePath);
  const isPropertyPath = activePath.includes('/properties/');
  const isSubPropertyPath = activePath.includes('/sub-properties/');
  const categoryLabel = hierarchy.find(
    label => label === 'Waqf' || label === 'Trust',
  );
  const isUnregistered = hierarchy.some(
    label => label.toLowerCase() === 'unregistered',
  );

  if (!hasCustomBreadcrumbItems && pageTitle) {
    const fallbackParts = pageTitle === 'Dashboard' ? [pageTitle] : hierarchy;
    return fallbackParts.length ? fallbackParts : [pageTitle];
  }

  if (!mazarName) {
    return pageTitle ? [titleCaseEntity(pageTitle)] : hierarchy;
  }

  if (pageTitle === 'Choose Category') {
    return [mazarName, 'Choose Category'];
  }

  if (isSubPropertyPath) {
    const subPropertyName = activeLabel || lastLabel;
    return [mazarName, 'SubProperty', subPropertyName].filter(Boolean);
  }

  if (isPropertyPath || pageTitle === 'Properties') {
    const propertyName =
      pageTitle !== 'Properties'
        ? pageTitle
        : activeLabel !== mazarName && activeLabel;

    return [mazarName, 'Property', propertyName].filter(Boolean);
  }

  if (isLandDetailPath) {
    return [mazarName, 'Land', activeLabel || lastLabel].filter(Boolean);
  }

  if (pageTitle === 'Lands') {
    if (isUnregistered) {
      return [mazarName, 'Land', 'Unregistered'];
    }

    if (categoryLabel) {
      const registrationNumber =
        activeLabel && activeLabel !== categoryLabel ? activeLabel : '';

      return [mazarName, categoryLabel, registrationNumber].filter(Boolean);
    }

    return [mazarName, 'Land'];
  }

  if (pageTitle === 'Registration Board' && categoryLabel) {
    const registrationNumber =
      activeLabel && activeLabel !== categoryLabel ? activeLabel : '';

    return [mazarName, categoryLabel, registrationNumber].filter(Boolean);
  }

  if (hierarchy.length) {
    return hierarchy;
  }

  return pageTitle ? [titleCaseEntity(pageTitle)] : [];
};

const BreadCrumb = ({
  title,
  pageTitle,
  parentRoute,
  breadcrumbItems,
}: BreadCrumbProps) => {
  // If breadcrumbItems is provided, use it; otherwise, fall back to the old format
  const items =
    breadcrumbItems ||
    (pageTitle && parentRoute
      ? [
          { label: pageTitle, path: `/${parentRoute}` },
          { label: title, isActive: true },
        ]
      : [{ label: title, isActive: true }]);

  const browserTitleParts = getBrowserTitleParts(
    items,
    title,
    Boolean(breadcrumbItems),
  );
  const browserTitle = browserTitleParts.join(' - ');

  useEffect(() => {
    if (browserTitle) {
      document.title = `MPM - ${browserTitle}`;
    }
  }, [browserTitle]);

  return (
    <React.Fragment>
      <Row>
        <Col xs={12}>
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <div className="page-title-left">
              <ol className="breadcrumb m-0">
                {items.map((item, index) => (
                  <li
                    key={index}
                    className={`breadcrumb-item ${item.isActive ? 'active' : ''}`}
                  >
                    {item.path && !item.isActive ? (
                      <Link to={item.path} style={{ color: 'gray' }}>
                        {item.label}
                      </Link>
                    ) : (
                      <span style={{ color: 'black' }}>{item.label}</span>
                    )}
                  </li>
                ))}
              </ol>
            </div>

            <div className="page-title-right">
              <h4 className="mb-sm-0">{title}</h4>
            </div>
          </div>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default BreadCrumb;
